/**
 * ============================================================================
 * TABLE SKELETON — shimmer placeholder for a metric table while it loads.
 * ============================================================================
 *
 * Production pattern: heavy tabular content shows a skeleton that mirrors the
 * final layout (a header strip + a few rows) rather than a bare spinner, so the
 * page keeps its shape and the wait feels shorter. Light async (a button, a
 * small panel) keeps using a spinner — see QueryStateGuard / CalloutPanel.
 *
 * Purely presentational and accessible: the whole block is one polite live
 * region labelled "Loading", and the shimmer bars are decorative.
 */

import React from 'react';

interface TableSkeletonProps {
  /** Placeholder body rows. */
  rows?: number;
  /** Placeholder columns (metric label + value columns). */
  columns?: number;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 6, columns = 6 }) => (
  <div className="table-skeleton" role="status" aria-live="polite" aria-busy="true" aria-label="Loading table">
    <div className="table-skeleton__row table-skeleton__row--header">
      {Array.from({ length: columns }).map((_, c) => (
        <span key={c} className="table-skeleton__cell" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="table-skeleton__row">
        {Array.from({ length: columns }).map((_, c) => (
          <span
            key={c}
            className="table-skeleton__cell"
            // First column (metric name) is wider; value columns are narrower.
            style={{ flex: c === 0 ? 2 : 1 }}
          />
        ))}
      </div>
    ))}
    <span className="table-skeleton__sr-only">Loading…</span>
  </div>
);
